import { assertNever } from "../obj";

import { DataExtractor, DataExtractorGroup } from "./extractor";

/**
 * A "specification" to build a regular data extractor.
 */
interface SimpleDataExtractorSpec<ITEM, VAL> {
    type: "Simple";
    extractor: DataExtractor<ITEM, VAL>;
}

/**
 * A "specification" to build an extractor which depends on other extractors.
 */
interface DependentDataExtractorSpec<ITEM, VAL> {
    type: "Dependent";
    /** The list of ids of extractors the resulting extractor depends on. */
    dependentExtractorIds: Array<string>;
    /** The id of the extdractor to build. */
    extractorId: string;
    /**
     * Given a hash mapping extractor ids to extractors, builds a data
     * extractor. The hash should contain the extractors listed in
     * {dependentExtractorIds}.
     */
    builder: (
        extractorIdToExtractor: ExtractorIdToExtractor<ITEM>
    ) => DataExtractor<ITEM, VAL>;
}

interface ExtractorIdToExtractor<ITEM> {
    [extractorId: string]: DataExtractor<ITEM, unknown>;
}

/**
 * A "specification" to build a simple or a dependent extractor.
 */
type DataExtractorSpec<ITEM, VAL> =
    | SimpleDataExtractorSpec<ITEM, VAL>
    | DependentDataExtractorSpec<ITEM, VAL>;

/**
 * A "specification" to build a group of data extractors.
 */
interface DataExtractorGroupSpec<ITEM> {
    id: string;
    label: string;
    extractorSpecs: Array<DataExtractorSpec<ITEM, any>>;
}

/**
 * Given a list of extractor specifications, builds the extractors.
 *
 * @return a hash of extractor id -> extractor.
 */
function resolveDataExtractorDependencies<ITEM>(
    specs: Array<DataExtractorSpec<ITEM, any>>
): ExtractorIdToExtractor<ITEM> {
    if (specs.length === 0) {
        return {};
    }
    const foundExtractorIdToExtractor: ExtractorIdToExtractor<ITEM> = {};
    let totalFound = 0;
    let dependentExtractors: Array<DependentDataExtractorSpec<ITEM, any>> = [];
    specs.forEach((spec) => {
        switch (spec.type) {
            case "Simple": {
                const { extractor } = spec;
                foundExtractorIdToExtractor[extractor.id] = extractor;
                totalFound++;
                break;
            }
            case "Dependent":
                dependentExtractors.push(spec);
                break;
        }
    });
    if (totalFound === 0) {
        throw new Error("Need at least one SimpleDataExtractorSpec");
    }
    while (dependentExtractors.length > 0) {
        const newDependentDataExtractors: Array<
            DependentDataExtractorSpec<ITEM, any>
        > = [];
        dependentExtractors.forEach((depExt) => {
            const dependencies: ExtractorIdToExtractor<ITEM> = {};
            for (const dependencyId of depExt.dependentExtractorIds) {
                const dependency = foundExtractorIdToExtractor[dependencyId];
                if (dependency !== undefined) {
                    dependencies[dependencyId] = dependency;
                } else {
                    // Couldn't find the dependency this time
                    // Maybe better luck at the next iteration
                    newDependentDataExtractors.push(depExt);
                    return;
                }
            }
            // We found all the dependencies, build the extractor
            const newExtractor = depExt.builder(dependencies);
            if (newExtractor.id !== depExt.extractorId) {
                throw new Error(
                    "Expected extractor id " +
                        depExt.extractorId +
                        ", got " +
                        newExtractor.id
                );
            }
            foundExtractorIdToExtractor[newExtractor.id] = newExtractor;
        });
        if (dependentExtractors.length === newDependentDataExtractors.length) {
            // Did not find anything this iteration, give up
            throw new Error(
                "Can't resolve extractors, dependency loop or wrong dependency id?"
            );
        }
        dependentExtractors = newDependentDataExtractors;
    }
    return foundExtractorIdToExtractor;
}

/**
 * Get the extractor id from a specification.
 */
function getSpecExtractorId<ITEM>(spec: DataExtractorSpec<ITEM, any>): string {
    switch (spec.type) {
        case "Simple":
            return spec.extractor.id;
        case "Dependent":
            return spec.extractorId;
        default:
            return assertNever(spec);
    }
}

/**
 * Given a list of data extractor specifications, builds the corresponding
 * extractors and returns them in the same order.
 */
function buildDataExtractors<ITEM>(
    specs: Array<DataExtractorSpec<ITEM, any>>
): Array<DataExtractor<ITEM, any>> {
    const resolved = resolveDataExtractorDependencies(specs);
    return specs.map((spec) => resolved[getSpecExtractorId(spec)]);
}

/**
 * Given a list of data extractor groups specifications, builds the corresponding
 * extractor groups and returns them in the same order.
 */
function buildDataExtractorGroups<ITEM>(
    groupSpecs: Array<DataExtractorGroupSpec<ITEM>>
): Array<DataExtractorGroup<ITEM>> {
    const extractorSpecs = groupSpecs.reduce(
        (memo, groupSpec) => {
            return memo.concat(groupSpec.extractorSpecs);
        },
        [] as Array<DataExtractorSpec<ITEM, any>>
    );
    const resolved = resolveDataExtractorDependencies(extractorSpecs);
    return groupSpecs.map((groupSpec) => {
        return {
            id: groupSpec.id,
            label: groupSpec.label,
            extractors: groupSpec.extractorSpecs.map(
                (spec) => resolved[getSpecExtractorId(spec)]
            ),
        };
    });
}
export type {
    ExtractorIdToExtractor,
    SimpleDataExtractorSpec,
    DependentDataExtractorSpec,
    DataExtractorSpec,
    DataExtractorGroupSpec,
};
export { buildDataExtractors, buildDataExtractorGroups };
